import { useCallback, useEffect, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import useSearchParamsHandler from "./useSearchParamsHandler";
import {
  allPagesAtom,
  allProductsAtom,
  fetchProductsAtom,
  productPerPageAtom,
  productsInfoAtom,
  productsLoadingStatusAtom,
} from "../atom/groupListings";
import { useAtom } from "jotai";
import { periodChangeFlagAtom } from "../atom/priceHistory";

const useUpdateGroupListings = (locationState) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { params } = useSearchParamsHandler();
  const [, fetchProducts] = useAtom(fetchProductsAtom);
  const [productsLoadingStatus, setProductsLoadingStatus] = useAtom(
    productsLoadingStatusAtom,
  );
  const [productsInfo] = useAtom(productsInfoAtom);
  const currentPage = +searchParams.get("page") || 1;
  const [allPages] = useAtom(allPagesAtom);
  const [productPerPage] = useAtom(productPerPageAtom);
  const [allProducts] = useAtom(allProductsAtom);
  const [periodChangeFlag, setPeriodChangeFlag] = useAtom(periodChangeFlagAtom);

  const getProductGroupIdFromPath = useCallback((pathname) => {
    const pathnameParts = pathname.split("/");
    const lastPart = pathnameParts[pathnameParts.length - 1];
    const pgIdMatch = lastPart.match(/(?:^|-)(\d+)$/);

    return pgIdMatch ? pgIdMatch[1] : null;
  }, []);

  const removeFromSearch = useCallback(() => {
    if (!locationState) {
      const pgId = getProductGroupIdFromPath(location.pathname);

      if (pgId) {
        const params = new URLSearchParams(location.search);
        params.set("pgId", pgId);
        params.delete("period");
        return params.toString();
      }
      return location.search;
    }

    const queryString = locationState.split("?")[1];
    if (!queryString) {
      const pgId = getProductGroupIdFromPath(locationState);

      if (pgId) {
        const params = new URLSearchParams(location.search);
        params.set("pgId", pgId);
        params.delete("period");
        return params.toString();
      }
      return location.search;
    }

    const params = new URLSearchParams(queryString);
    params.delete("period");
    return params.toString();
  }, [locationState, location.pathname, location.search, getProductGroupIdFromPath]);

  const settingProducts = useCallback(
    async (location) => {
      try {
        if (!location) return;

        const urlParams = new URLSearchParams(location);
        const pgId = urlParams.get("pgId");

        if (pgId) {
          setProductsLoadingStatus("loading");
          await fetchProducts(location);
        }
      } catch (e) {
        console.error(e);
        setProductsLoadingStatus("idle");
      }
    },
    [fetchProducts, setProductsLoadingStatus],
  );

  useEffect(() => {
    if (!location.search.includes(`page=${currentPage}`)) {

      setSearchParams({ ...params, page: currentPage }, { replace: true });
    }
  }, [currentPage, location.search, params, setSearchParams]);

  useEffect(() => {
    const updatedSearch = removeFromSearch();
    if (updatedSearch) {
      settingProducts(updatedSearch);
    }
  }, [removeFromSearch, settingProducts]);

  useEffect(() => {
    if (!periodChangeFlag && locationState) {
      window.scrollTo(0, 0);
      const updatedSearch = removeFromSearch();
      if (updatedSearch) {
        settingProducts(updatedSearch);
      }
    }
    setPeriodChangeFlag(false);
  }, [
    location.search,
    locationState,
    periodChangeFlag,
    removeFromSearch,
    settingProducts,
    setPeriodChangeFlag,
  ]);

  const data = useMemo(
    () => ({
      currentPage,
      allPages,
      productsInfo,
      productPerPage,
      allProducts,
      productsLoadingStatus,
    }),
    [
      currentPage,
      allPages,
      productsInfo,
      productPerPage,
      allProducts,
      productsLoadingStatus,
    ],
  );

  return data;
};

export default useUpdateGroupListings;
